import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, Search, Trash2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDataCleanupPanel = () => {
  const [properties, setProperties] = useState([]);
  const [selectedPropId, setSelectedPropId] = useState("");
  const [loadingDetect, setLoadingDetect] = useState(false);
  const [loadingClean, setLoadingClean] = useState(false);
  const [anomalies, setAnomalies] = useState(null); // null = ainda não detectou
  const [lastCleanupResult, setLastCleanupResult] = useState(null);

  // Carrega propriedades do admin
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/bleeding-properties`);
        setProperties(r.data || []);
      } catch (e) {
        toast.error("Erro ao carregar propriedades");
      }
    };
    load();
  }, []);

  const detectar = useCallback(async () => {
    setLoadingDetect(true);
    setAnomalies(null);
    setLastCleanupResult(null);
    try {
      const params = selectedPropId ? { property_id: selectedPropId, dry_run: true } : { dry_run: true };
      const r = await axios.post(`${API}/admin/delivery-day-sangria-anomalies`, null, { params });
      setAnomalies(r.data);
      if (r.data.total_anomalies === 0) {
        toast.success("Nenhuma anomalia encontrada. Base limpa!");
      } else {
        toast.info(`${r.data.total_anomalies} anomalia(s) detectada(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao detectar anomalias");
    } finally {
      setLoadingDetect(false);
    }
  }, [selectedPropId]);

  const limpar = useCallback(async () => {
    if (!anomalies || anomalies.total_anomalies === 0) return;
    const propName = selectedPropId
      ? (properties.find(p => p.id === selectedPropId)?.name || "propriedade")
      : "TODAS as propriedades";
    const confirmMsg = `Tem certeza que quer LIMPAR ${anomalies.total_anomalies} anomalia(s) em ${propName}?\n\n` +
      `Esta ação:\n` +
      `• Remove APENAS os campos de sangria (sangria=false, sangrias=[])\n` +
      `• PRESERVA a produção (kg), coletas, reposições e nome do trabalhador\n` +
      `• Adiciona auditoria em cada registro\n\n` +
      `Não é reversível automaticamente. Prosseguir?`;
    if (!window.confirm(confirmMsg)) return;
    if (!window.confirm(`⚠️ CONFIRMAÇÃO FINAL: ${anomalies.total_anomalies} registro(s) serão alterados. Confirma?`)) return;

    setLoadingClean(true);
    try {
      const params = selectedPropId
        ? { property_id: selectedPropId, dry_run: false }
        : { dry_run: false };
      const r = await axios.post(`${API}/admin/delivery-day-sangria-anomalies`, null, { params });
      setLastCleanupResult(r.data);
      toast.success(`✅ ${r.data.updated} registro(s) limpo(s) com sucesso!`);
      // Re-detecta para confirmar
      setTimeout(() => detectar(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao limpar");
    } finally {
      setLoadingClean(false);
    }
  }, [anomalies, selectedPropId, properties, detectar]);

  return (
    <Card data-testid="admin-cleanup-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Limpeza de Anomalias em Dias de Entrega
        </CardTitle>
        <p className="text-sm text-gray-600 mt-1">
          Detecta e limpa <strong>duplicações reais</strong> de sangria — quando o mesmo <em>(data, lote)</em> aparece
          em 2+ registros da mesma propriedade. Em propriedades no modo &quot;equipe&quot;, a âncora legítima
          (uma única sangria/dia representando o time inteiro) <strong>NÃO é mais flagada</strong>.
          <br />
          <strong className="text-green-700">✓ Corrigido em Fev/2026</strong> — heurística ingênua anterior removida (causava falsos positivos e apagava sangrias legítimas).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Seleção de propriedade */}
        <div>
          <label className="block text-sm font-medium mb-1">Propriedade (opcional — vazio = todas)</label>
          <select
            data-testid="cleanup-property-select"
            value={selectedPropId}
            onChange={(e) => setSelectedPropId(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">— Todas as propriedades —</option>
            {properties.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Botões */}
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="cleanup-detect-btn"
            onClick={detectar}
            disabled={loadingDetect || loadingClean}
            variant="outline"
          >
            {loadingDetect ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
            Detectar anomalias
          </Button>

          <Button
            data-testid="cleanup-execute-btn"
            onClick={limpar}
            disabled={!anomalies || anomalies.total_anomalies === 0 || loadingDetect || loadingClean}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loadingClean ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Limpar {anomalies?.total_anomalies || 0} anomalia(s)
          </Button>
        </div>

        {/* Resultado da última limpeza */}
        {lastCleanupResult && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm" data-testid="cleanup-result">
            <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-600" />
            <strong>Limpeza concluída:</strong> {lastCleanupResult.updated} de {lastCleanupResult.total_anomalies} registro(s) alterado(s).
          </div>
        )}

        {/* Tabela de anomalias */}
        {anomalies && anomalies.total_anomalies > 0 && (
          <div className="overflow-x-auto" data-testid="cleanup-anomalies-table">
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="outline" className="text-amber-700 border-amber-400">
                {anomalies.total_anomalies} anomalia(s) detectada(s)
              </Badge>
              <span className="text-xs text-gray-500">
                (Dry-run: nada foi alterado ainda)
              </span>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-2 border">Data</th>
                  <th className="p-2 border">Propriedade</th>
                  <th className="p-2 border">Trabalhador</th>
                  <th className="p-2 border">Sangria (lote=valor)</th>
                  <th className="p-2 border">Reposição</th>
                  <th className="p-2 border text-right">Produção (kg)</th>
                </tr>
              </thead>
              <tbody>
                {anomalies.anomalies.map((a) => (
                  <tr key={a.id} className="hover:bg-amber-50">
                    <td className="p-2 border font-mono text-xs">{a.date}</td>
                    <td className="p-2 border">{a.property_name || "—"}</td>
                    <td className="p-2 border">{a.trabalhador || "—"}</td>
                    <td className="p-2 border">
                      <span className="text-red-700 font-medium" title="Será removida">
                        {a.sangria_lote || "—"}
                      </span>
                    </td>
                    <td className="p-2 border">
                      {a.reposicao_valor > 0
                        ? (
                          <span className="text-green-700 font-medium" title="PRESERVADA — não será removida">
                            ✓ {a.reposicao_valor} {a.reposicao_lote}
                          </span>
                        )
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="p-2 border text-right font-mono">{a.producao_kg.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-600 mt-2">
              <span className="text-red-700 font-medium">Vermelho</span> = será removido &nbsp;·&nbsp;
              <span className="text-green-700 font-medium">Verde ✓</span> = preservado (reposição legítima)
            </p>
          </div>
        )}

        {anomalies && anomalies.total_anomalies === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm" data-testid="cleanup-empty">
            <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-600" />
            Nenhuma anomalia encontrada. Base está limpa!
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDataCleanupPanel;
